/**
 * Read the ink a large brand surface has settled on.
 *
 * Text on those surfaces just uses the `text-band-ink` / `text-banner-ink`
 * utilities. This hook is for the things CSS cannot colour — picking which
 * variant of a logo image to show, for instance, since brand artwork keeps its
 * own colours and can only be swapped, never recoloured.
 */
import { useEffect, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { INK_DARK, INK_LIGHT, subscribeBranding } from '../lib/branding';

export type SurfaceName = 'band' | 'banner';

function readInk(surface: SurfaceName, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const value = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(`--color-${surface}-ink`)
    .trim();
  return value || fallback;
}

/**
 * The surface's current ink as an `'r g b'` triplet. Recomputed when the theme
 * flips or the workspace changes its colours.
 */
export function useSurfaceInk(surface: SurfaceName): string {
  const { theme } = useTheme();
  const fallback = theme === 'dark' ? INK_LIGHT : INK_DARK;
  const [ink, setInk] = useState(() => readInk(surface, fallback));

  useEffect(() => {
    // The variables are written to the root element, so a frame after the
    // theme class flips is enough for the new value to be readable.
    const sync = () => setInk(readInk(surface, fallback));
    const frame = requestAnimationFrame(sync);
    const stop = subscribeBranding(() => requestAnimationFrame(sync));
    return () => {
      cancelAnimationFrame(frame);
      stop();
    };
  }, [surface, theme, fallback]);

  return ink;
}

/** True when the surface wants light text — i.e. the surface itself is dark. */
export function useSurfaceIsDark(surface: SurfaceName): boolean {
  return useSurfaceInk(surface) === INK_LIGHT;
}
